/*
 * Xifres públiques del vertical de contractació, en un sol lloc.
 *
 * PER QUÈ EXISTEIX
 * ----------------
 * Fins a l'11/10/2026 els números de /contratacion-publica i de la card §03
 * de la landing eren literals escrits a mà. Cada refresc de la dada els
 * deixava mentint, i va passar: la pàgina deia 43.910 contratos i 1.529 M€
 * mentre la dada n'eren 46.473 i 1.579 M€. Pitjor encara, hi havia TRES
 * xifres diferents al mateix repo (34.984 en un comentari, 43.910 a la
 * description, 25.379 a la landing), totes defensables en el seu moment i
 * cap igual a cap altra.
 *
 * Ara hi ha dues fonts i les dues les genera la mateixa passada del seed:
 *
 *   1. src/data/contratacion_stats.json — instantània compromesa al repo.
 *      La fa seed_contratacion_municipal.py. Serveix per al PRIMER PINTAT i
 *      per a la description del <head>, que no poden esperar una crida
 *      asíncrona. És un literal, sí, però ja no el manté ningú.
 *   2. GET /api/contratacion/stats — la dada viva. Substitueix la
 *      instantània quan arriba, així la pàgina és correcta també entre
 *      desplegaments.
 *
 * Si l'API no respon, es queda la instantània: és la del darrer build, no
 * una xifra de fa tres mesos.
 */
import { useEffect, useState } from "react";
import SNAPSHOT from "../data/contratacion_stats.json";
import SEO_COPY from "../data/contratacion_seo.json";

const API = process.env.REACT_APP_BACKEND_URL;

export const statsSnapshot = SNAPSHOT;
export const seoCopy = SEO_COPY;

// ── Formats ────────────────────────────────────────────────────────────

/**
 * Separador de milers propi, i NO `toLocaleString('es-ES')`.
 *
 * Dues raons, les dues comprovades al navegador:
 *
 *   1. En castellà, l'agrupació per defecte NO s'aplica als números de
 *      quatre dígits. `(3007).toLocaleString('es-ES')` dona «3007», i la
 *      pàgina mostrava «De los 3007 contratos de bar», «1579 M€» i «4554
 *      organismos» al costat de «46.473», que en té cinc. Inconsistent dins
 *      de la mateixa frase.
 *   2. Aquest comportament ha canviat entre versions de V8, de manera que
 *      dos visitants amb navegadors diferents podien veure textos
 *      diferents. Per a una xifra que és una afirmació pública, això no es
 *      pot deixar a la implementació de torn.
 *
 * (La norma de la RAE efectivament no separa els de quatre dígits, però
 * aquí mana la coherència de la taula: tots els KPI amb el mateix format.)
 */
const milers = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export const fmtInt = (n) => (typeof n === "number" ? milers(Math.round(n)) : "—");

/** Euros -> «1.579 M€». Sense decimals: a aquesta escala no diuen res. */
export const fmtMEur = (eur) =>
  typeof eur === "number" ? `${milers(Math.round(eur / 1e6))} M€` : "—";

/** 199.6 -> «199,6». Decimal amb coma, que és el que toca en castellà. */
export const fmtDec1 = (n) =>
  typeof n === "number"
    ? `${milers(Math.trunc(n))},${Math.abs(Math.round(n * 10) % 10)}`
    : "—";

/** 0.7818 -> «78%». */
export const fmtPct = (x) =>
  typeof x === "number" ? `${Math.round(x * 100)}%` : "—";

export const fmtMilesAbajoRaw = (n) =>
  typeof n === "number" ? milers(Math.floor(n / 1000) * 1000) : "—";

/**
 * 19.886 -> «19.000+». Arrodonir A LA BAIXA, no al més proper.
 *
 * Les empreses són un sostre i no un recompte exacte (el camp del pipeline
 * no fusiona totes les variants d'un mateix nom), i per tant l'única
 * afirmació que aguanta és «com a mínim». Un «20.000+» seria recolzar-se en
 * la xifra inflada.
 */
export const fmtMilesAbajo = (n) =>
  typeof n === "number" ? `${fmtMilesAbajoRaw(n)}+` : "—";

// ── Derivats ───────────────────────────────────────────────────────────

/**
 * Anys COMPLETS de la sèrie, que no són tots els que hi ha.
 *
 * El primer any del corpus és sempre una cua de la finestra de descàrrega
 * (2017 són 47 files) i l'últim és sempre l'any en curs, encara a mitges
 * (2026 arriba al setembre tancat). Pintar-los faria semblar que el mercat
 * arrenca de zero i després s'ensorra, i això no és el que diu la dada.
 *
 * Per això es retalla un any per cada banda. La regla es manté sola: el
 * gener que entri el 2027, el 2026 passarà a ser un any complet tot sol.
 */
export const yearSeries = (s) => {
  const todos = s?.por_anio || [];
  if (todos.length <= 2) return todos;
  return todos.slice(1, -1);
};

/** «×4,6 más contratos en 2025 vs 2018», calculat de la sèrie que es pinta. */
export const growthLabel = (serie) => {
  if (!serie || serie.length < 2) return null;
  const a = serie[0];
  const b = serie[serie.length - 1];
  if (!a?.contratos) return null;
  const x = b.contratos / a.contratos;
  return {
    factor: `×${x.toLocaleString("es-ES", { maximumFractionDigits: 1 })}`,
    desde: a.anio,
    hasta: b.anio,
  };
};

export const ccaaTop = (s, n = 6) => (s?.por_ccaa || []).slice(0, n);

/**
 * Composa la description de /contratacion-publica.
 *
 * La frase viu a data/contratacion_seo.json perquè la MATEIXA frase l'ha de
 * compondre també scripts/build-seo-manifest.js, en Node, per al <head> que
 * injecta el servidor. Els rastrejadors i els scrapers socials que no
 * executen JavaScript només veuen aquell, i si la frase visqués aquí i prou,
 * a aquesta ruta se'ls seguiria servint la description genèrica de la home.
 *
 * La substitució són tres línies i es fan dues vegades (aquí i al generador),
 * però el CONTINGUT és d'un sol lloc. És el repartiment que es pot defensar:
 * duplicar un `replace` no fa que la web digui dues coses; duplicar la frase,
 * sí.
 */
export const seoDescription = (s = SNAPSHOT) =>
  SEO_COPY.description
    .replace("{contratos}", fmtInt(s?.contratos))
    .replace("{importe}", fmtMEur(s?.importe_total_eur))
    .replace("{empresas}", fmtMilesAbajo(s?.empresas));

/** Import d'una activitat (Socorrismo, Mantenimiento, Bar / cafetería). */
export const importeActividad = (s, nombre) => {
  const hit = (s?.por_actividad || []).find(
    (a) => (a.actividad || "").toLowerCase() === nombre.toLowerCase()
  );
  return hit ? hit.importe_eur : null;
};

export const kpis = (s) => [
  { value: fmtInt(s?.contratos), label: "contratos públicos analizados" },
  { value: fmtMEur(s?.importe_total_eur), label: "importe adjudicado" },
  { value: fmtInt(s?.empresas), label: "empresas adjudicatarias" },
  { value: fmtInt(s?.organismos), label: "organismos públicos" },
  { value: fmtInt(s?.provincias), label: `provincias · ${fmtInt(s?.ccaa)} CCAA` },
  { value: `${s?.anio_min}–${s?.anio_max}`, label: "años de histórico" },
];

// ── Hook ───────────────────────────────────────────────────────────────

/**
 * Instantània primer, dada viva quan arribi.
 *
 * No hi ha estat de càrrega a propòsit: no hi ha cap moment en què la
 * pàgina mostri un número buit o equivocat. I si l'API falla, no es toca
 * res — un `catch` que esborrés les xifres convertiria una API caiguda en
 * una pàgina sense dades.
 */
export const useContratacionStats = () => {
  const [stats, setStats] = useState(SNAPSHOT);

  useEffect(() => {
    if (!API) return; // en local no hi ha backend: es queda la instantània
    const ctrl = new AbortController();
    fetch(`${API}/api/contratacion/stats`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.contratos === "number") setStats(d);
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  return stats;
};
